import { Brand, Dictionary, State } from "../../../interfaces";
import { Card as CardBase, Popconfirm as PopconfirmBase, Row, Tag as TagBase, Button, Modal as ModalBase } from "antd";
import {
  CaretUpOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FolderViewOutlined
} from "@ant-design/icons";
import { Item, ItemFile } from "../../../reducers/items";
import React, { useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

import DownloadBtn from "../downloadbtn";
import DynamicIcon from "../../DynamicIcon";
import FileLoader from "../../FileLoader";
import { FiltersState } from "../../../reducers/filters";
import { Language, LanguageState } from "../../../reducers/language";
import ItemTag from "../itemtag";
import { Link } from "react-router-dom";
import ShareBtn from "../share/shareBtn";
import { Theme } from "../../../theme";
import { formatDate } from "../../../utils";

// ponytail: antd4's Card/Popconfirm/Tag/ModalProps predate React 18's stricter children typing; cast until antd5 migration
const Card: any = CardBase;
const Popconfirm: any = PopconfirmBase;
const Tag: any = TagBase;
const Modal: any = ModalBase;

// import { useWhyDidYouUpdate } from "../../hooks/useWhyDidYouUpdate";

/*interface Item {
  id: string;
  thumbUrl: string;
  name: string;
  icon: string;
  publishDate: string;
  can_edit: boolean;
  tags: string[];
  markets: string[];
  typologyName: string;
  typologyIcon: string;
  files: Array<ItemFile>;
}*/

interface Props {
  item: Item;
  noLabel: string;
  yesLabel: string;
  downloadLabel: string;
  unpublishWarningLabel: string;
  addremovetags: string;
  tagById?: Dictionary<any>;
  marketById?: Dictionary<any>;
  userCanShare: boolean;
  editMode: boolean;
  onItemSelect: Function;
  onTagClicked?: Function;
  onRemoveItem?: Function;
  onShare?: Function;
  onOpenEditModal?: Function;
  theme: Theme;
}

interface ItemProps {
  id: string;
  unpublishWarningLabel: string;
  noLabel: string;
  yesLabel: string;
  can_edit: boolean;
  editMode: boolean;
  removeItem?: Function;
}

const UpublishBtn = ({
  id,
  noLabel,
  yesLabel,
  unpublishWarningLabel,
  editMode,
  can_edit,
  removeItem
}: ItemProps) => {
  const _onClick = () => {
    if (removeItem) removeItem(id);
  };

  return editMode && can_edit ? (
    <Popconfirm
      placement="bottomRight"
      title={unpublishWarningLabel}
      onConfirm={_onClick}
      okText={yesLabel}
      cancelText={noLabel}
    >
      <CloseCircleOutlined className="unpublish" />
    </Popconfirm>
  ) : null;
};

const ThumbItem: React.FC<Props> = (props) => {
  const {
    item,
    tagById,
    marketById,
    downloadLabel,
    unpublishWarningLabel,
    addremovetags,
    noLabel,
    yesLabel,
    editMode,
    userCanShare,
    onItemSelect,
    onShare,
    onTagClicked,
    onRemoveItem,
    onOpenEditModal,
    theme
  } = props;

  const {
    id,
    name,
    desc,
    thumbUrl,
    icon,
    publishDate,
    tags,
    markets,
    typologyName,
    typologyIcon,
    files,
    can_edit,
    externalUrl,
    openInApp
  } = item;

  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  const selectedBrand: Brand | null = useSelector(
    (state: State) => state.filters.selectedBrand,
    shallowEqual
  );

  const selectedLanguage: Language | null = useSelector(
    (state: State) => state.language.selectedLanguage,
    shallowEqual
  );

  const userMarkets: string[] = useSelector(
    (state: State) => state.auth.user?.markets ?? [],
    shallowEqual
  );

  const maxMarketsBeforeClipping = 4;
  const [clipMarkets, setClipMarkets] = useState(
    markets.length > maxMarketsBeforeClipping
  );
  const [isModalVisible, setIsModalVisible] = useState(false);

  const selectItem = () => {
    onItemSelect(item);
  };

  const handleOnShare = () => {
    if (onShare) onShare(item);
  };

  const handleOnOpenEditModal = () => {
    if (onOpenEditModal) onOpenEditModal(item);
  };

  const toggleClip = () => {
    setClipMarkets((state) => !state);
  };

  const handleExternalUrlClick = () => {
    if (openInApp) {
      setIsModalVisible(true);
    } else {
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  /**
   * Handles communication between the parent and its iframe.
   * Responds to the data requests from the iframe
   * by sending the stored auth and other app contexts.
   * Allows the modal to be closed from within the iframe by listening for
   * a postMessage event with either 'closeModal' or { type: 'closeModal' }.
   */
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "REQUEST_AUTH") {
        const auth = localStorage.getItem("auth");
        if (iframeRef.current?.contentWindow && auth) {
          iframeRef.current.contentWindow.postMessage(
            { type: "AUTH", payload: auth },
            event.origin
          );
        }
      } else if (event.data?.type === "REQUEST_APP_CONTEXT") {
        const lang = selectedLanguage;
        const brand = selectedBrand;
        const markets = userMarkets;
        iframeRef.current?.contentWindow?.postMessage(
          { type: "APP_CONTEXT", payload: { lang, brand, markets } },
          event.origin
        );
      } else if (event.data === "closeModal" || event.data?.type === "closeModal") {
        handleCloseModal();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [selectedBrand, selectedLanguage, userMarkets]);

  return (
    <>
      <div
        onClick={externalUrl && openInApp ? handleExternalUrlClick : undefined}
        style={externalUrl && openInApp ? { cursor: 'pointer' } : undefined}
      >
        <Card
          className="thumb"
          title={
            <div className="card-title">
              <DynamicIcon type={icon} /> <p>{name}</p>
            </div>
          }
          headStyle={{ backgroundColor: props.theme.primary }}
          bodyStyle={{ padding: 0 }}
          extra={
            <UpublishBtn
              id={id}
              can_edit={can_edit}
              editMode={editMode}
              removeItem={onRemoveItem}
              unpublishWarningLabel={unpublishWarningLabel}
              noLabel={noLabel}
              yesLabel={yesLabel}
            />
          }
        >
          {thumbUrl && thumbUrl !== "" && (
            <Link to={`/${selectedBrand?.code}/details/${id}`}>
              <div className="custom-image" onClick={selectItem}>
                <FileLoader fileName={name} fileUrl={thumbUrl} type="tag" />
                <Row className="img-overlay" align="middle" justify="center">
                  <EyeOutlined className="icon" />
                </Row>
              </div>
            </Link>
          )}
          <div className="custom-card">
            {externalUrl ? (
              <div className={`details-bar`}>
                <p>{desc}</p>
              </div>
            ) : (
              <div className={`details-bar${clipMarkets ? " clip" : ""}`}>
                <h4>{formatDate(publishDate)}</h4>
                {marketById &&
                  markets.map((market, index) => {
                    return marketById[market] !== undefined ? (
                      clipMarkets && index >= maxMarketsBeforeClipping ? null : (
                        <Tag
                          className="market-tag"
                          key={market}
                          id={market}
                          color={theme.primary}
                        >
                          {marketById[market].name}
                        </Tag>
                      )
                    ) : null;
                  })}
                {markets.length > maxMarketsBeforeClipping && (
                  <Tag className="market-tag show-more" onClick={toggleClip}>
                    {!clipMarkets ? <CaretUpOutlined /> : "..."}
                  </Tag>
                )}
              </div>
            )}
            {editMode && (
              <Tag
                className="add-remove"
                color={theme.primary}
                onClick={handleOnOpenEditModal}
              >
                {addremovetags}
              </Tag>
            )}
            {/* <div className="tag-wrapper">
          {tagById &&
            tags.map((tag) => {
              return tagById[tag] !== undefined ? (
                <ItemTag
                  key={tag}
                  id={tag}
                  itemId={id}
                  name={tagById[tag].name}
                  tagClicked={onTagClicked}
                />
              ) : (
                console.log("tag non presente nella lista ", tag)
              );
            })}
        </div> */}
            {typologyName && typologyIcon &&
              <div className="typology-bar">
                <DynamicIcon type={typologyIcon} />
                <h4 className="typology-name">{typologyName}</h4>
              </div>
            }

            <div className="btns-wrapper" style={{ justifyContent: 'center', padding: '10px 0' }}>
              {externalUrl ? (
                <div>
                  <Button
                    type="primary"
                    shape="circle"
                    style={{ backgroundColor: theme.primary }}
                    icon={<FolderViewOutlined />}
                    size="large"
                  />
                </div>
              ) : (
                <>
                  <DownloadBtn
                    direction="topCenter"
                    clsName="btn-white"
                    files={files}
                    itemID={id}
                    label={downloadLabel}
                    theme={theme}
                    bulkDownload={true}
                  />
                  {userCanShare && (
                    <ShareBtn
                      clsName="btn-white"
                      files={files}
                      itemID={id}
                      onShare={handleOnShare}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Modal
        visible={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        closable={false}
        maskClosable={false}
        width="100vw"
        mask={false}
        transitionName="fade"
        maskTransitionName=""
        style={{
          top: 64,
          maxWidth: '100vw',
          paddingBottom: 0,
          margin: 0
        }}
        bodyStyle={{
          height: 'calc(100vh - 64px)',
          padding: 0
        }}
        wrapClassName="no-shadow-modal no-pointer-events"
        destroyOnClose
      >
        <iframe
          ref={iframeRef}
          src={externalUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          title={name}
        />
      </Modal>
    </>
  );
};

export default React.memo<Props>(ThumbItem);
